/* eslint-disable no-restricted-globals */
import React, { useState } from 'react';
import { Modal, withStyles } from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  deletePerforatorItem,
  setPerforatorValues,
  restoreOriginalPerforator,
  createPerforatorItem,
  updatePerforatorList,
  searchPerforatorList,
} from '~/store/dailyparts/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import { useTranslation } from 'react-i18next';
import { defaultItem, filterPerforatorList } from './utils';
import {
  diffTwoDateTimeStr,
  parseDate,
  parseDateToTimestamp,
  subtractDaysFromNow,
  newMomentDate,
  formatDate,
  formatTime,
} from '~/utils/moment';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';

const calculateDuration = row => diffTwoDateTimeStr(
  row.start_date || formatDate(newMomentDate()),
  row.start_time || formatTime(newMomentDate()),
  row.end_date || formatDate(newMomentDate()),
  row.end_time || formatTime(newMomentDate()),
) / 60000;

const durationIsValid = row => ({ isValid: calculateDuration(row) >= 0 });

const renderDuration = (row) => {
  const duration = calculateDuration(row);
  const color = durationIsValid(row) ? '' : 'red';
  return <span style={{ color }}>{duration.toFixed(2)}</span>;
};

const PERFORATOR_EQUIP_IDS = [4, 11, 16, 17, 18, 19];
const FANDRILL_EQUIPS = [16, 17, 18, 19];
const JUMBO_OPERATION_TYPE_WITH_ROD_NUMBER = [11005];
const JUMBO = 11;
const ROCK_BOLT = 18;

const PerforatorList = () => {
  const { t: translate } = useTranslation();

  const perforatorList = useSelector(
    state => state.dailyParts.perforatorList,
  );
  const filters = useSelector(state => state.dailyParts.filters);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const perforatorGroups = useSelector(state => (
    state.manager.equipmentsGroups.filter(eg => PERFORATOR_EQUIP_IDS.includes(eg.id_equipament))
  ), shallowEqual);
  const perforators = useSelector(state => state.manager.equipments
    .filter(equip => PERFORATOR_EQUIP_IDS.includes(equip.id_equip)), shallowEqual);
  const operationTypes = useSelector(state => state.manager.operationTypes
    .filter(e => PERFORATOR_EQUIP_IDS.includes(e.id_equipment_type)), shallowEqual);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const drillholeTypes = useSelector(state => state.manager.drillholeTypes);
  const fireIds = useSelector(state => state.manager.fireIds.filter(o => o.name), shallowEqual);
  const fandrillFans = useSelector(state => state.manager.fandrillFans);
  const fandrillBitDiametersNames = useSelector(state => state.manager.fandrillBitDiametersNames);
  const fandrillBitDiameters = useSelector(state => state.manager.fandrillBitDiameters);
  const boomReasons = useSelector(state => state.manager.boomReasons);
  const closures = useSelector(state => state.manager.closures);
  const perforatorToolsLinks = useSelector(state => state.manager.perforatorToolsLinks);
  const originalPerforatorList = useSelector(state => state.dailyParts.originalPerforatorList);
  const perforatorPagination = useSelector(
    state => state.dailyParts.pagination.perforatorPagination,
  );

  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const jumboHoleDepthCalc = ({
    rowData,
    keyToSearch,
    keyToChange,
  }) => {
    const nDrillHoles = rowData[keyToSearch];
    const {
      rod_depth: rodDepth,
      operation_type_id: operationType,
      rod_number: rodNumber,
    } = rowData;

    if (!nDrillHoles || !rodDepth) return 0;

    const shouldUseRodNumber = JUMBO_OPERATION_TYPE_WITH_ROD_NUMBER.includes(operationType);
    if (!shouldUseRodNumber) {
      // eslint-disable-next-line no-param-reassign
      rowData[keyToChange] = nDrillHoles * rodDepth;
      return nDrillHoles * rodDepth;
    }

    // eslint-disable-next-line no-param-reassign
    rowData[keyToChange] = nDrillHoles * rodNumber * rodDepth;
    return nDrillHoles * rodNumber * rodDepth;
  };

  const dispatch = useDispatch();

  const validateNumericField = key => (rowData) => {
    const value = rowData[key];

    if (['', NaN].includes(value) || !Number.isInteger(Number(value))) {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }

    return { isValid: true };
  };

  const validateFloatField = key => (rowData) => {
    const value = String(rowData[key] ?? '').trim();

    if (value === '' || value === '.') {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }
    const regex = /^\d+([.]\d+)?$/;

    if (!regex.test(value)) {
      return { isValid: false, helperText: translate('validation:mustBeNumeric') };
    }
    return { isValid: true };
  };

  const validateLookupField = key => (rowData) => {
    if (['', '0', 0].includes(rowData[key])) {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }

    return { isValid: true };
  };

  const validateDate = key => (row) => {
    const currentDatetime = newMomentDate();

    const stDate = row.start_date || formatDate(currentDatetime);
    const stTime = row.start_time || formatTime(currentDatetime);
    const endDate = row.end_date || formatDate(currentDatetime);
    const endTime = row.end_time || formatTime(currentDatetime);

    const startDateTimestamp = parseDateToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateToTimestamp(endDate, endTime);

    const isEnableClosureOn = enableClosure && closures?.length > 0;
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure;
    }

    if (key === 'end_date' && startDateTimestamp > endDateTimestamp) {
      return { isValid: false, helperText: translate('validation:FinalDateSmallerThanInitialDate') };
    }

    const invalidEditPeriodDateMsg = {
      isValid: false, helperText: translate('validation:InvalidPeriodDate'),
    };

    if (key === 'start_date' && parseDate(row[key]).isBefore(userLimitEdit)) return invalidEditPeriodDateMsg;

    return { isValid: true };
  };

  const validateDateOrTimeField = key => (row) => {
    const fieldValue = row[key];

    const invalidRequiredFieldMsg = {
      isValid: false, helperText: translate('validation:RequiredField'),
    };

    const invalidDefaultValidation = !fieldValue || fieldValue.includes('Invalid');
    const invalidTimeField = invalidDefaultValidation || String(fieldValue).includes('_');
    const invalidDateField = invalidDefaultValidation || fieldValue.length === 0 || !String(fieldValue).includes('/');

    if (key.includes('time') && invalidTimeField) return invalidRequiredFieldMsg;
    if (key.includes('date') && invalidDateField) return invalidRequiredFieldMsg;

    const isValidDate = validateDate(key)(row);
    if (!isValidDate.isValid) return isValidDate;

    return { isValid: true };
  };

  const validateRodNumber = ({ rod_number: rn, operation_type_id: operationType }) => {
    const rodNumber = Number(rn);
    const shouldUseRodNumber = JUMBO_OPERATION_TYPE_WITH_ROD_NUMBER.includes(operationType);

    if (isNaN(rodNumber) || rodNumber < 0) {
      return { isValid: false, helperText: translate('validation:InvalidRodNumber') };
    }

    if (!shouldUseRodNumber && rodNumber > 0) {
      return { isValid: false, helperText: translate('validation:CantDefineRodNumber') };
    }

    return { isValid: true, helperText: '' };
  };

  const validateScreensNumber = ({ screens_number: sn }) => {
    const screensNumber = Number(sn);

    if (isNaN(screensNumber) || screensNumber < 0) {
      return { isValid: false, helperText: translate('validation:InvalidScreensNumber') };
    }

    return { isValid: true, helperText: '' };
  };

  const perforatorColumns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: validateDateOrTimeField('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      validate: validateDateOrTimeField('end_date'),
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      validate: validateDateOrTimeField('start_time'),
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      validate: validateDateOrTimeField('end_time'),
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartDate'),
      field: 'drilling_start_date',
      customType: 'date',
      validate: validateDateOrTimeField('drilling_start_date'),
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartTime'),
      field: 'drilling_start_time',
      customType: 'time',
      validate: validateDateOrTimeField('drilling_start_time'),
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      render: renderDuration,
      validate: durationIsValid,
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns.map(e => ({
        id: e.id,
        name: `${e.turn_group_name} - ${e.name}`,
        active: e.active,
      })),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: perforatorGroups,
      customType: 'filterLookup',
      lookupFilter: option => String(option.id_equipament) === String(filters.equipmentType)
        || String(filters.equipmentType) === '0',
      tooltip: true,
      required: true,
      validate: validateLookupField('equip_group_id'),
      customOnChange: () => ({
        equip_id: '0',
        fandrill_bit_diameter_id: '0',
        fandrill_bit_diameter_name_id: '0',
        drilling_type: '0',
        hammer_id: '0',
        rod_id: '0',
        bit_holder_id: '0',
        breech_id: '0',
        handle_id: '0',
        tube_id: '0',
        grinding_wheel_id: '0',
        shock_absorber_id: '0',
      }),
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: perforators,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
      required: true,
      validate: validateLookupField('equip_id'),
      customOnChange: () => ({
        hammer_id: '0',
        rod_id: '0',
        bit_holder_id: '0',
        breech_id: '0',
        handle_id: '0',
        tube_id: '0',
        grinding_wheel_id: '0',
        shock_absorber_id: '0',
      }),
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes.filter(ot => [filters.equipmentType].includes(ot.id_equipment_type)),
      customType: 'filterLookup',
      tooltip: true,
      required: true,
      validate: validateLookupField('operation_type_id'),
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      tooltip: true,
      required: true,
      validate: validateLookupField('operator_group_id'),
      customOnChange: () => ({ operator_id: '0' }),
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'operator_id',
      lookup: operators,
      lookupKey: 'id_operator',
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.operator_group_id)
        || String(row.operator_group_id) === '0',
      tooltip: true,
      required: true,
      validate: validateLookupField('operator_id'),
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'origin_id',
      lookup: elements,
      tooltip: true,
      required: true,
      validate: validateLookupField('origin_id'),
      customOnChange: () => ({ point_id: '0' }),
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      customType: 'filterLookup',
      field: 'point_id',
      lookupFilter: (option, row) => String(option.id_element) === String(row.origin_id)
        || String(row.origin_id) === '0',
      lookup: subElements,
      tooltip: true,
      required: true,
      validate: validateLookupField('point_id'),
    },
    {
      title: translate('common:Material'),
      field: 'material_id',
      customType: 'filterLookup',
      lookup: materials,
      tooltip: true,
      required: true,
      validate: validateLookupField('material_id'),
    },
    {
      editable: 'always',
      title: translate('common:FireID'),
      field: 'fire_id',
      lookup: fireIds,
      customType: 'filterLookup',
      tooltip: true,
      required: true,
      validate: validateLookupField('fire_id'),
    },
    {
      editable: 'always',
      title: translate('common:DrillingType'),
      field: 'drilling_type',
      lookup: drillholeTypes,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.equipment_group_id)
        === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
      required: true,
      validate: validateLookupField('drilling_type'),
    },
    {
      editable: 'always',
      title: translate('common:BitDiameter'),
      field: 'fandrill_bit_diameter_id',
      lookup: fandrillBitDiameters,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.equipment_group_id) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
      required: true,
      validate: validateLookupField('fandrill_bit_diameter_id'),
      customOnChange: () => ({ fandrill_bit_diameter_name_id: '0' }),
    },
    {
      editable: 'always',
      title: translate('common:BitDiameterName'),
      field: 'fandrill_bit_diameter_name_id',
      lookup: fandrillBitDiametersNames,
      lookupFilter: (option, row) => String(option.fandrill_bit_diameter_id)
        === String(row.fandrill_bit_diameter_id)
        || String(row.fandrill_bit_diameter_id) === '0',
      customType: 'filterLookup',
      tooltip: true,
      required: true,
      validate: validateLookupField('fandrill_bit_diameter_name_id'),
    },
    {
      editable: 'always',
      title: translate('common:DrillingCount'),
      field: 'n_drillholes',
      required: true,
      type: 'numeric',
      align: 'left',
      validate: validateNumericField('n_drillholes'),
    },
    {
      editable: 'always',
      title: translate('common:DrillingDepth'),
      field: 'hole_depth',
      required: true,
      validate: validateFloatField('hole_depth'),
    },
    {
      editable: 'always',
      title: translate('common:HoleID'),
      field: 'hole_id',
      required: true,
      type: 'numeric',
      align: 'left',
      validate: validateNumericField('hole_id'),
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'idle_time',
    },
    {
      editable: 'always',
      title: translate('common:DrillingDuration'),
      field: 'drilling_time',
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'X',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'Y',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'Z',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Hammer'),
      field: 'hammer_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '1'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Rod'),
      field: 'rod_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '2'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:BitHolder'),
      field: 'bit_holder_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '3'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Breech'),
      field: 'breech_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '4'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Handle'),
      field: 'handle_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '5'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Tube'),
      field: 'tube_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '6'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:GrindingWheel'),
      field: 'grinding_wheel_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '7'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:ShockAbsorber'),
      field: 'shock_absorber_id',
      customType: 'filterLookup',
      lookup: perforatorToolsLinks,
      lookupFilter: (option, row) => (
        String(row.equip_id) !== '0'
        && String(option.equip_id) === String(row.equip_id)
        && String(option.tool_type_id) === '8'
      ),
      tooltip: true,
    },
  ];

  const fandrillColumns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: validateDate('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
    },
    {
      title: translate('common:DrillingStartDate'),
      field: 'drilling_start_date',
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartTime'),
      field: 'drilling_start_time',
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      render: renderDuration,
      validate: durationIsValid,
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns.map(e => ({
        id: e.id,
        name: `${e.turn_group_name} - ${e.name}`,
        active: e.active,
      })),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: perforatorGroups,
      customType: 'filterLookup',
      lookupFilter: option => String(option.id_equipament) === String(filters.equipmentType)
        || String(filters.equipmentType) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: perforators,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes.filter(ot => [filters.equipmentType].includes(ot.id_equipment_type)),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'operator_id',
      lookup: operators,
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookupFilter: (option, row) => String(option.id_group) === String(row.operator_group_id)
        || String(row.operator_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'origin_id',
      lookup: elements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      customType: 'filterLookup',
      field: 'point_id',
      lookupFilter: (option, row) => String(option.id_element) === String(row.origin_id)
        || String(row.origin_id) === '0',
      lookup: subElements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      field: 'material_id',
      customType: 'filterLookup',
      lookup: materials,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:FireID'),
      field: 'fire_id',
      lookup: fireIds,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DrillingType'),
      field: 'drilling_type',
      lookup: drillholeTypes,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.equipment_group_id)
        === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Fan'),
      field: 'fandrill_fan_id',
      lookup: fandrillFans,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.equipment_group_id) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:HolePosition'),
      field: 'rod_position',
    },
    {
      editable: 'always',
      title: translate('common:BitDiameter'),
      field: 'fandrill_bit_diameter_id',
      lookup: fandrillBitDiameters,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_group) === String(row.equipment_group_id)
        || String(row.equipment_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:RodFootage'),
      field: 'rod_depth',
    },
    {
      editable: 'always',
      title: translate('common:DrillingCount'),
      field: 'n_drillholes',
    },
    {
      editable: 'always',
      title: translate('common:RodNumber'),
      field: 'rod_number',
    },
    {
      editable: 'always',
      title: translate('common:DrillingDepth'),
      field: 'hole_depth',
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'idle_time',
    },
    {
      editable: 'always',
      title: translate('common:DrillingDuration'),
      field: 'drilling_time',
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'X',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'Y',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'Z',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
      tooltip: true,
    },
  ];

  const jumboColumns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: validateDate('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartDate'),
      field: 'drilling_start_date',
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartTime'),
      field: 'drilling_start_time',
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      render: renderDuration,
      validate: durationIsValid,
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns.map(e => ({
        id: e.id,
        name: `${e.turn_group_name} - ${e.name}`,
        active: e.active,
      })),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: perforatorGroups,
      customType: 'filterLookup',
      lookupFilter: option => String(option.id_equipament) === String(filters.equipmentType)
        || String(filters.equipmentType) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: perforators,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes.filter(ot => [filters.equipmentType].includes(ot.id_equipment_type)),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'operator_id',
      lookup: operators,
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookupFilter: (option, row) => String(option.id_group) === String(row.operator_group_id)
        || String(row.operator_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'origin_id',
      lookup: elements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      customType: 'filterLookup',
      field: 'point_id',
      lookupFilter: (option, row) => String(option.id_element) === String(row.origin_id)
        || String(row.origin_id) === '0',
      lookup: subElements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      customType: 'filterLookup',
      field: 'material_id',
      lookup: materials,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:FireID'),
      field: 'fire_id',
      lookup: fireIds,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DrillingType'),
      field: 'drilling_type',
      lookup: drillholeTypes,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.equipment_group_id)
        === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:RodFootage'),
      field: 'rod_depth',
    },
    {
      editable: 'always',
      title: translate('common:NumOfBoons'),
      field: 'num_of_booms',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:BitDiameter'),
      field: 'fandrill_bit_diameter_id',
      lookup: fandrillBitDiameters,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.equipment_group_id) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:BitDiameterName'),
      field: 'fandrill_bit_diameter_name_id',
      lookup: fandrillBitDiametersNames,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.fandrill_bit_diameter_id) === String(row.fandrill_bit_diameter_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:BitDiameterSecond'),
      field: 'fandrill_bit_diameter_id_second',
      lookup: fandrillBitDiameters,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.equipment_group_id) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:BitDiameterNameSecond'),
      field: 'fandrill_bit_diameter_name_id_second',
      lookup: fandrillBitDiametersNames,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.fandrill_bit_diameter_id) === String(row.fandrill_bit_diameter_id_second)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:RodNumber'),
      field: 'rod_number',
      validate: validateRodNumber,
    },
    {
      editable: 'always',
      title: translate('common:DrillingCount'),
      field: 'n_drillholes',
    },
    {
      editable: 'always',
      title: translate('common:DrillingCountSecond'),
      field: 'n_drillholes_second',
    },
    {
      editable: 'never',
      title: translate('common:DrillingDepth'),
      field: 'hole_depth',
      render: rowData => (
        jumboHoleDepthCalc({
          rowData,
          keyToChange: 'hole_depth',
          keyToSearch: 'n_drillholes',
        })
      ),
    },
    {
      editable: 'never',
      title: translate('common:DrillingDepthSecond'),
      field: 'hole_depth_second',
      render: rowData => (
        jumboHoleDepthCalc({
          rowData,
          keyToChange: 'hole_depth_second',
          keyToSearch: 'n_drillholes_second',
        })
      ),
    },
    {
      editable: 'always',
      title: translate('common:ScreenCount'),
      field: 'screens_number',
      validate: validateScreensNumber,
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'idle_time',
    },
    {
      editable: 'always',
      title: translate('common:DrillingDuration'),
      field: 'drilling_time',
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'X',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'Y',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'Z',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Reason'),
      field: 'use_partial_boom_reason_id',
      lookup: [
        { id: 0, name: translate('common:None') },
        ...boomReasons.map(e => ({
          id: e.id,
          name: e.reason,
        })),
      ],
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:NumberOfDowels'),
      field: 'arches_number',
      type: 'numeric',
      validate: validateNumericField('arches_number'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:NumberOfSheets'),
      field: 'lattice_girder_number',
      type: 'numeric',
      validate: validateNumericField('lattice_girder_number'),
      required: true,
    },
  ];

  const rockBoltColumns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: validateDate('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
    },
    {
      title: translate('common:DrillingStartDate'),
      field: 'drilling_start_date',
    },
    {
      editable: 'always',
      title: translate('common:DrillingStartTime'),
      field: 'drilling_start_time',
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      render: renderDuration,
      validate: durationIsValid,
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns.map(e => ({
        id: e.id,
        name: `${e.turn_group_name} - ${e.name}`,
        active: e.active,
      })),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: perforatorGroups,
      customType: 'filterLookup',
      lookupFilter: option => String(option.id_equipament) === String(filters.equipmentType)
        || String(filters.equipmentType) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: perforators,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.id_group) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes.filter(ot => [filters.equipmentType].includes(ot.id_equipment_type)),
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'operator_id',
      lookup: operators,
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      lookupFilter: (option, row) => String(option.id_group) === String(row.operator_group_id)
        || String(row.operator_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'origin_id',
      lookup: elements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      customType: 'filterLookup',
      field: 'point_id',
      lookupFilter: (option, row) => String(option.id_element) === String(row.origin_id)
        || String(row.origin_id) === '0',
      lookup: subElements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      field: 'material_id',
      customType: 'filterLookup',
      lookup: materials,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:FireID'),
      field: 'fire_id',
      lookup: fireIds,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DrillingType'),
      field: 'drilling_type',
      lookup: drillholeTypes,
      customType: 'filterLookup',
      lookupFilter: (option, row) => String(option.equipment_group_id)
        === String(row.equip_group_id)
        || String(row.equip_group_id) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Fan'),
      field: 'fandrill_fan_id',
      lookup: fandrillFans,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.equipment_group_id) === String(row.equip_group_id)
        || String(row.equip_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:HolePosition'),
      field: 'rod_position',
    },
    {
      editable: 'always',
      title: translate('common:BitDiameter'),
      field: 'fandrill_bit_diameter_id',
      lookup: fandrillBitDiameters,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_group) === String(row.equipment_group_id)
        || String(row.equipment_group_id) === '0'
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:RodFootage'),
      field: 'rod_depth',
    },
    {
      editable: 'always',
      title: translate('common:DrillingCount'),
      field: 'n_drillholes',
    },
    {
      editable: 'always',
      title: translate('common:RodNumber'),
      field: 'rod_number',
    },
    {
      editable: 'always',
      title: translate('common:ScreenCount'),
      field: 'screens_number',
    },
    {
      editable: 'always',
      title: translate('common:NumberOfArches'),
      field: 'arches_number',
      type: 'numeric',
      validate: validateNumericField('arches_number'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:NumberOfLatticeGirders'),
      field: 'lattice_girder_number',
      type: 'numeric',
      validate: validateNumericField('lattice_girder_number'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:DrillingDepth'),
      field: 'hole_depth',
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'idle_time',
    },
    {
      editable: 'always',
      title: translate('common:DrillingDuration'),
      field: 'drilling_time',
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'X',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'Y',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'Z',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
      tooltip: true,
    },
  ];

  const setColumns = () => {
    let columnList = perforatorColumns;
    if (FANDRILL_EQUIPS.includes(filters.equipmentType)) {
      columnList = fandrillColumns;
    }
    if (JUMBO === filters.equipmentType) columnList = jumboColumns;
    if (ROCK_BOLT === filters.equipmentType) columnList = rockBoltColumns;

    return columnList;
  };

  const filterToFormFieldMap = {
    turn: 'turn',
    equipment: 'equip_id',
    equipmentGroup: 'equip_group_id',
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    material: 'material_id',
    startOrigin: 'origin_id',
    startSubOrigin: 'point_id',
  };

  const filterToFormMultiFieldMap = {
    equipmentGroup: 'equip_group_id',
    equipment: 'equip_id',
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      if (filters[filterKey] !== undefined && filters[filterKey] !== 0) {
        acc[formField] = filters[filterKey];
      }
      return acc;
    },
    {},
  );

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValues = filters[filterKey];

      // adiciona para initialFormData somente
      // se tiver apenas um item selecionado no multiselect
      if (filterValues && filterValues.length === 1) {
        const [firstValue] = filterValues;
        acc[formField] = firstValue;
      } else {
      // se tiver mais de um ou nenhum, seta como '0'
        acc[formField] = '0';
      }
      return acc;
    },
    {},
  );

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'perforator',
    pagination: perforatorPagination,
    searchAction: searchPerforatorList,
  });

  const { page = 0, total = 0, loadingMore = false } = perforatorPagination || { };

  const initialFormData = {
    ...perforatorColumns.reduce((acc, e) => ({ ...acc, [e.field]: '0' }), {}),
    ...defaultItem,
    ...mappedFilterData,
    ...mappedFilterMultiData,
  };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        initialFormData={initialFormData}
        columns={applyDefaultSort(setColumns())}
        data={perforatorList}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          search: true,
          selection: true,
          cacheKey: 'daily_parts_perforator',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={(newData) => {
          dispatch(createPerforatorItem(newData));
        }}
        onDiscard={() => {
          dispatch(restoreOriginalPerforator());
        }}
        onApply={() => {
          const filteredDataList = filterPerforatorList(perforatorList);
          dispatch(updatePerforatorList({
            data: filteredDataList,
            parameters: filters,
            ogData: originalPerforatorList,
            saveLog: true,
          }));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            dispatch(
              setPerforatorValues({
                ...row,
                duration: calculateDuration(row),
              }),
            );
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deletePerforatorItem(e));
          });
        }}
      />

      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="perforator"
          referenceColumns={setColumns()}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

export default withStyles(styles)(PerforatorList);
